import React, { useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RiDeleteBinLine, RiPencilLine } from "@remixicon/react";

const LabelActions = ({
  type,
  itemKey,
  itemLabel,
  handleItemDelete,
  sectionId,
  setParentOpen,
}: {
  type: "label" | "description";
  itemKey: string;
  itemLabel: string;
  accountId: string | undefined;
  handleItemDelete: () => Promise<void>;
  sectionId?: string;
  setParentOpen?: (open: boolean) => void;
}) => {
  const [, setSearchParams] = useSearchParams();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setParentOpen?.(false);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("type", type);
      params.set("id", itemKey);
      if (sectionId) {
        params.set("sectionId", sectionId);
      }
      return Object.fromEntries(params.entries());
    });
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log("handleDelete", e);
    try {
      setDeleting(true);
      e.preventDefault();
      e.stopPropagation();
      await handleItemDelete();
      // Close the dialog after successful deletion
      setShowDeleteAlert(false);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          className="group-hover:opacity-100 data-[state=open]:opacity-100 opacity-0 shrink-0 overflow-hidden"
        >
          <MoreVertical
            className="stroke-[1.5] ml-auto cursor-pointer shrink-0 text-brand-primary-400"
            size={14}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="right"
          className="p-1 border-0 bg-transparent overflow-hidden relative squircle squircle-fill-[white] squircle-radius-[12px] squircle-smooth-[1] before:absolute before:inset-0 before:squircle before:squircle-radius-[12px] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none"
        >
          <DropdownMenuItem
            onClick={handleEdit}
            className="px-[8px] py-[6px] border-0  hover:bg-selected-gray flex items-center gap-[6px]"
          >
            <RiPencilLine size={14} className="text-brand-primary-new/40" />
            <span className="relative top-[1px] text-[12px] text-brand-primary-new/80">
              Edit
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteAlert(true);
            }}
            className="px-[8px] py-[4px] border-0 hover:text-[#E32124] text-[#E32124] hover:bg-[#E321240F] flex items-center gap-[6px]"
          >
            <RiDeleteBinLine size={14} />
            <span className="relative top-[1px]">Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <DialogContent
          className="!rounded-[12px]"
          onClick={(e) => e.stopPropagation()}
          hideCloseButton
        >
          <DialogHeader>
            <DialogTitle className="text-[#344054] font-inter text-[16px] font-semibold leading-[28px]">
              Are you absolutely sure?
            </DialogTitle>
            <DialogDescription className="text-[#71717A] font-geist text-[13px] font-normal leading-[20px]">
              Confirm deletion of the{" "}
              <span className="text-[#344054] font-geist text-[13px] font-medium leading-[20px]">
                '{itemLabel}'
              </span>{" "}
              {type}. This will permanently remove the {type} and its data, and may affect
              any reports using it. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setShowDeleteAlert(false);
              }}
              className="text-[12px]"
            >
              Cancel
            </Button>
            <Button
              onClick={(e) => void handleDelete(e)}
              className="min-w-[69px] bg-[#D92D20] hover:bg-[#D92D20]/80 text-white border-0"
            >
              {deleting ? <LuLoaderCircle className="h-4 w-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LabelActions;
